import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { SignSheet } from '../components/SignSheet'
import { cancelTrade, tradeStatus, loadTradeLimits } from '../lib/api'
import { useLiveRows, pollEvery } from '../lib/live'
import { fmtC, type Employee, type TradeLimits } from '../lib/types'
import { openPos } from './SellCard'
import type { TradeDoneState } from './TradeReview'
import { DoneMark } from '../components/DoneMark'
import { StarWait } from '../components/StarWait'

/** A card-settled trade waiting for Shopify POS: the outgoing pieces are in the Starchive tile; staff enter the trade-in credit as a Custom payment, then the card. */
export function TradeCard() {
  const nav = useNav()
  const s = useLocation().state as TradeDoneState | null
  const [status, setStatus] = useState<Awaited<ReturnType<typeof tradeStatus>>>(null)
  const [limits, setLimits] = useState<TradeLimits | null>(null)
  const [cancel, setCancel] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => { if (!s) nav('/trade', { replace: true }) }, [s, nav])
  useEffect(() => { loadTradeLimits().then(setLimits).catch(() => {}) }, [])
  // the trade row is heard live (L2); the poll stays as the fallback, slowed while the live link is up
  const tickRef = useRef<() => void>(() => {})
  const live = useLiveRows('trades', s ? `id=eq.${s.trade_id}` : null, () => tickRef.current())
  useEffect(() => {
    if (!s) return
    let on = true
    const tick = async () => { const r = await tradeStatus(s.trade_id).catch(() => null); if (on && r) setStatus(r) }
    tickRef.current = tick
    tick(); const t = setInterval(tick, pollEvery(live))
    const vis = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', vis)
    return () => { on = false; clearInterval(t); document.removeEventListener('visibilitychange', vis) }
  }, [s, live])
  useEffect(() => { if (s && status?.status === 'paid') nav('/trade/done', { state: { ...s, status: 'paid', order: status.shopify_order_name, mismatch: status.mismatch ? status.mismatch_note : '' }, replace: true }) }, [status, s, nav])
  if (!s) return null
  const cancelled = status?.status === 'cancelled'
  const tender = limits?.trade_tender_name ?? 'Trade-in credit'
  const doCancel = async (e: Employee) => {
    setBusy(true); setErr('')
    try { await cancelTrade(s.trade_id, e.employee_id); setCancel(false) } catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }
  return (
    <Page bar={<TopBar title="Card payment" caption={`Trade #${s.trade_no}`} />}>
      <div className="done">
        {cancelled ? <DoneMark kind="cancelled" /> : <StarWait />}
        <div className="done__title">{cancelled ? 'Cancelled' : 'Waiting for the card'}</div>
        <div className="done__sub">Trade #{s.trade_no} · pieces {fmtC(s.out_total_cents)} · signed by {s.by.name} ({s.by.employee_id}). Nothing has moved yet.</div>
        {!cancelled && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, width: '100%', marginTop: 8 }}>
              <div className="card" style={{ padding: '14px 16px' }}><div className="hint">1 · {tender}, as a Custom payment</div><div style={{ fontSize: 40, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtC(s.in_total_cents)}</div><div className="hint">Enter this exact amount. The app checks it against the order.</div></div>
              <div className="card" style={{ padding: '14px 16px', background: '#000', color: '#fff' }}><div className="hint" style={{ color: '#c7c7cc' }}>2 · Card, the rest</div><div style={{ fontSize: 40, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtC(s.difference_cents)}</div><div className="hint" style={{ color: '#c7c7cc' }}>POS shows this as the balance once the credit is in.</div></div>
            </div>
            <div className="card done__list">
              <div className="done__item"><span className="dot ok" style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>1</span><span>Open Shopify POS, tap the <b>Starchive</b> tile, then <b>Put in cart</b>. The pieces land at list price, discounted to the agreed prices, labelled Trade #{s.trade_no}</span></div>
              <div className="done__item"><span className="dot ok" style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>2</span><span>Tap <b>Charge</b>, then <b>Custom</b> → <b>{tender}</b>, and enter <b>{fmtC(s.in_total_cents)}</b></span></div>
              <div className="done__item"><span className="dot ok" style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>3</span><span>Take the card for the balance. This screen updates itself when Shopify confirms</span></div>
            </div>
          </>
        )}
        {err && <div className="banner" style={{ background: '#d70015', marginTop: 12 }}>{err}</div>}
        <div className="done__btns">
          {!cancelled ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setCancel(true)}>Cancel trade</button>
              <button type="button" className="cta cta--center" onClick={openPos}>Open Shopify POS</button>
            </>
          ) : (
            <>
              <button type="button" className="btn-secondary" onClick={() => nav('/')}>Home</button>
              <button type="button" className="cta cta--center" onClick={() => nav('/trade')}>New trade</button>
            </>
          )}
        </div>
        {!cancelled && <div className="hint" style={{ maxWidth: 560, textAlign: 'center', marginTop: 14 }}>Card declined or the customer walked? Cancel: nothing was charged, no stock moved, no cards printed.</div>}
      </div>
      {cancel && <SignSheet title={`Cancel trade #${s.trade_no}`} onConfirm={doCancel} onClose={() => !busy && setCancel(false)} busy={busy} />}
    </Page>
  )
}
